import { useCallback, useEffect, useState } from "react";
import { ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { getEvents, setEventArchived, type Event } from "@/services/api/events";
import { formatDateShort } from "@/utils/eventUtils";

interface ArchivedEventsSectionProps {
  currentLocale: string;
  refreshKey: number;
  onUnarchived: () => void;
}

/**
 * JUB-157: archived events are hidden from the main list; this toggle shows
 * them with an "Unarchive" action.
 */
export function ArchivedEventsSection({ currentLocale, refreshKey, onUnarchived }: ArchivedEventsSectionProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [items, setItems] = useState<Event[] | null>(null);
  // A failed load is shown as an error, not as "no archived events" (JUB-157 D2).
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    getEvents({ archived: true })
      .then(setItems)
      .catch(() => {
        setItems(null);
        setLoadError(true);
      });
  }, []);

  useEffect(() => {
    if (show) load();
  }, [show, load, refreshKey]);

  const handleUnarchive = async (uuid: string) => {
    try {
      await setEventArchived(uuid, false);
      toast.success(t("events.archive.unarchived"));
      load();
      onUnarchived();
    } catch {
      toast.error(t("events.archive.error"));
    }
  };

  return (
    <section className="mt-6" data-testid="archived-section">
      <Button variant="link" size="sm" className="h-auto px-0 py-1" onClick={() => setShow(!show)} data-testid="archived-toggle">
        {show ? t("events.archive.hide") : t("events.archive.show")}
      </Button>
      {show && loadError && (
        <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm" role="alert" data-testid="archived-load-error">
          <p>{t("events.archive.loadError")}</p>
          <Button size="sm" variant="outline" className="mt-2" onClick={load} data-testid="archived-load-retry">
            {t("common.retry")}
          </Button>
        </div>
      )}
      {show && items && (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-muted-foreground">{t("events.archive.hint")}</p>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("events.archive.empty")}</p>
          ) : (
            <ul className="space-y-2">
              {items.map((event) => (
                <li
                  key={event.uuid}
                  className="flex items-center justify-between gap-3 rounded-md border bg-background p-3"
                  data-testid="archived-item"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{event.name}</p>
                    <p className="text-xs text-muted-foreground">{event.date ? formatDateShort(event.date, currentLocale) : t('events.dateTbd')}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleUnarchive(event.uuid)} data-testid="unarchive-button">
                    <ArchiveRestore className="mr-1 h-3.5 w-3.5" />
                    {t("events.archive.unarchive")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
